<template>
  <div
    v-style:bg="backgroundColor"
    :class="[
      {
        'bg-black c-white': isSummitRunClubVariant,
        'bg-brand-1 c-white': isBasecamp2026SharedVariant,
      },
      textColor,
    ]"
  >
    <div class="pb-4">
      <div v-if="isBasecamp2026Variant" class="relative md:z-20 md:p-6 md:pb-0">
        <div class="p-4">
          <div class="hidden gap-4 md:flex md:items-center md:between">
            <div class="flex flex-1 gap-4 wrap">
              <div
                v-for="({ label, options }, index) in eventFilters"
                :key="label"
                style="max-width: 12rem; min-width: 9.5rem"
              >
                <vf-dropdown
                  class-button="w-full o:rounded-sm c-black px-4 between ws-nowrap"
                  :label
                  :style-content="{ minWidth: '13rem', maxHeight: '18rem', zIndex: 10 }"
                >
                  <ul class="grid gap-3" :class="[options.length > 6 ? 'cols-2' : 'cols-1']">
                    <li v-for="{ value, label: optionLabel } in options" :key="`${filterKeys[index]}-${value}`">
                      <vf-checkbox v-model="selectedFilters[filterKeys[index]]" :value>
                        {{ optionLabel }}
                      </vf-checkbox>
                    </li>
                  </ul>
                </vf-dropdown>
              </div>
            </div>
            <p class="text-sm c-white">
              {{ filteredEvents.length }} {{ $t.events }}
            </p>
          </div>
          <div class="flex items-center gap-4 between md:hidden">
            <div class="flex flex-1 items-center gap-2">
              <base-button
                class="h-11 flex shrink-0 items-center gap-2 b rounded-sm bg-white px-3 c-black"
                @click="openMobileFilters"
              >
                <span class="text-sm">{{ $t.filter }}</span>
                <vf-icon name="filters" size="md" />
              </base-button>
            </div>
            <p class="text-right text-sm c-white">
              {{ filteredEvents.length }} {{ $t.events }}
            </p>
          </div>
          <div v-if="hasActiveFilters" class="mt-6 flex items-start gap-4 c-white">
            <div class="flex flex-1 gap-2 wrap">
              <template v-for="({ label: filterLabel }, index) in eventFilters" :key="`selected-${filterLabel}`">
                <div
                  v-for="item in selectedFilters[filterKeys[index]]"
                  :key="`${filterKeys[index]}-${item}`"
                  class="flex items-center gap-2 px-3 text-sm"
                >
                  <span>{{ item }}</span>
                  <base-button
                    :aria-label="$t.remove"
                    class="hover:op-70"
                    @click="removeSelectedFilter(filterKeys[index], item)"
                  >
                    <vf-icon name="close" size="md" />
                  </base-button>
                </div>
              </template>
            </div>
            <base-button
              class="text-sm underlined"
              @click="clearAllFilters"
            >
              {{ $t.clearAll }}
            </base-button>
          </div>
          <div v-if="hasActiveFilters" class="mt-6 b-b b-white -mx-4 md:-mx-6" />
        </div>
        <modal-mobile-filters class="z-above-header" />
      </div>
      <div v-else>
        <div
          v-if="!isLoyaltyDashboardVariant && filters?.length > 1"
          class="flex center py-8 fw-light <md:hidden"
          :class="{ 'justify-center wrap': isViewMore }"
        >
          <base-button
            class="ws-nowrap b-r b-$theme px-10 py-2 text-sm duration hover:underlined"
            :class="{ 'underlined': !activeFilter, 'b-grey-30 uppercase': (isSummitRunClubVariant || isUTMBVariant) }"
            @click="activeFilter = ''"
          >
            {{ $t.viewAllEvents }}
          </base-button>
          <base-button
            v-for="(filter, key) in truncatedFilters"
            :key="filter.value"
            class="max-w-1/2 truncate px-10 py-2 text-sm duration hover:underlined"
            :class="{
              'b-r b-$theme': key < truncatedFilters.length - 1,
              'underlined': filter.value === activeFilter,
              'b-grey-30 uppercase': (isSummitRunClubVariant || isUTMBVariant),
            }"
            @click="activeFilter = filter.value"
          >
            {{ filter.label }}
          </base-button>
          <base-button
            v-if="filters.length > 3"
            class="ws-nowrap b-l b-$theme px-10 py-2 text-sm underlined"
            :class="{ 'b-grey-30 uppercase': (isSummitRunClubVariant || isUTMBVariant) }"
            @click="isViewMore = !isViewMore"
          >
            <span>{{ isViewMore ? $t.viewLess : `${$t.viewMore} (${viewMoreLinkCount})` }}</span>
          </base-button>
        </div>
        <div v-if="!isLoyaltyDashboardVariant && filters.length > 1" class="p-4 md:hidden" :class="{ 'px-6': isUTMBVariant }">
          <vf-select
            v-model="activeFilter"
            :options="filters"
            :placeholder="$t.viewAllEvents"
            v-bind="(isSummitRunClubVariant || isUTMBVariant)
              ? {
                class: `b-white ${isUTMBVariant ? 'o:bg-brand-1' : 'o:bg-black'}`,
                iconClass: 'o:c-white',
                textClass: 'o:c-white',
                labelClass: `px-1 ${isUTMBVariant ? 'o:bg-brand-1' : 'o:bg-black'}`,
              }
              : {}"
          >
            <span :class="{ 'c-white': (isSummitRunClubVariant || isUTMBVariant) }">{{ $t.events }}</span>
          </vf-select>
        </div>
      </div>
      <div v-if="variant === 'climb-festival'" class="overflow-hidden">
        <transition name="slide">
          <vf-event-card-climb :event-simplified :events="filteredEvents" />
        </transition>
      </div>
      <div
        v-else-if="isLoyaltyDashboardVariant"
        class="overflow-hidden bg-white p-6 c-black <md:px-4"
      >
        <h2 class="mb-6 title-2">
          {{ $t.events }}
        </h2>
        <base-grid
          class="gap-2"
          :cols="{ sm: 1, md: 2, lg: 2 }"
        >
          <transition-group name="slide">
            <div
              v-for="({ date, image, ...event }, key) in displayedEvents"
              :key
              class="h-full"
              :class="{
                '<md:hidden': !isLoyaltyDashboardExpanded && key > 1,
                'md:hidden': !isLoyaltyDashboardExpanded && key > 3,
              }"
            >
              <vf-event-card
                class="h-full"
                :community-event="event.communityEvent"
                :date
                :image
                :is-past-event="isPastEvent(event as EventCollectionItem)"
                :location="formatLoyaltyLocation(event as EventCollectionItem)"
                :title="event.title"
                :title-html="event.titleHtml"
                variant="loyalty-dashboard-event"
                @show-event="open(event as EventCollectionItem)"
              />
            </div>
          </transition-group>
        </base-grid>
        <div v-if="hasMoreLoyaltyMobileEvents || hasMoreLoyaltyDesktopEvents" class="flex center pt-6">
          <vf-button v-if="hasMoreLoyaltyMobileEvents" class="md:hidden" variant="tertiary" @click="loadMore">
            {{ $t.showAll }}
          </vf-button>
          <vf-button v-if="hasMoreLoyaltyDesktopEvents" class="<md:hidden" variant="tertiary" @click="loadMore">
            {{ $t.showAll }}
          </vf-button>
        </div>
      </div>
      <div
        v-else
        class="overflow-hidden p-6"
        :class="{
          'bg-brand-1 c-white pt-0 p-6': isBasecamp2026SharedVariant,
        }"
      >
        <div
          v-if="isBasecamp2026SharedVariant && !filteredEvents.length"
          class="flex center px-6 py-12 text-center md:px-0 md:py-16"
        >
          <p class="text-md c-brand-7 md:text-xl">
            {{ noEventsInMarket }}
          </p>
        </div>
        <base-grid
          v-else
          class="md:-mx-4"
          :cols="{ sm: 1, md: 2, lg: 3 }"
        >
          <transition-group name="slide">
            <div
              v-for="({ date, image, locationName, ...event }, key) in displayedEvents"
              :key
              class="slide-up b-b"
              :class="{
                'b-grey-30': isSummitRunClubVariant,
                'b-white': isBasecamp2026SharedVariant,
                'b-$theme': !isSummitRunClubVariant && !isBasecamp2026SharedVariant,
              }"
            >
              <vf-event-card
                :date
                :image
                :is-past-event="isPastEvent(event as EventCollectionItem)"
                :title="event.title"
                :title-html="event.titleHtml"
                v-bind="isSummitRunClubVariant
                  ? { location: locationName, variant: 'summit-running-club' }
                  : isBasecamp2026SharedVariant
                    ? {
                      location: event.locations?.[0]?.name,
                      variant: isUTMBVariant ? 'UTMB' : 'basecamp-2026',
                      srcIcon: event.srcIcon,
                      eventType: event.eventType,
                      communityEvent: event.communityEvent,
                    }
                    : { label: event.locations?.[0]?.countryCode }"
                @show-event="open(event as EventCollectionItem)"
              />
            </div>
          </transition-group>
        </base-grid>
        <div v-if="hasMoreEvents" class="flex center py-8">
          <base-button class="text-md c-white underlined" @click="loadMore">
            {{ loadMoreEvents }}
          </base-button>
        </div>
      </div>
      <modal-event-detail
        class="z-above-header"
        :class-content="['o:p-0 o:h-full o:max-h-full o:overflow-y-auto !w-full', { 'overflow-hidden o:bg-brand-1': isBasecamp2026DialogVariant, 'o:bg-black': isSummitRunClubVariant }]"
        disable-outside-interaction
        size="full"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { EventCollectionContent } from '#types/components/cms/event-collection'
import type { FilterDefinition, FilterState } from '#types/components/cms/event-collection-filters'
import type { EventCollectionItem } from '#types/components/cms/event-collection-item'

const props = defineProps<{ content: EventCollectionContent }>()
const { eventSimplified, items = [], variant, backgroundColor, proximityEnabled } = props.content

const { $t, $locale } = useNuxtApp()
const userCountryCode = useCountryCode()
const eventQuery = useRouteQuery('event')
const edgescapeLatLong = useCookie('Edgescape-Lat-Long')
const { coords, error, isSupported, resume, pause } = useGeolocation({ immediate: false })

const userCoordinates = computed(() => proximityEnabled
  ? resolveUserCoordinates({ browser: coords.value, cookie: edgescapeLatLong.value })
  : null
)

onMounted(() => {
  if (!proximityEnabled || !isSupported.value) return
  resume()
  watchOnce([coords, error], () => pause())
})

const isViewMore = ref(false)
const activeFilter = ref('')

const { data, error: err } = await useApi().internal.getStaticPage('basecamp2026')
const needsBasecampStatic = variant === 'basecamp-2026' || variant === 'UTMB'
if (needsBasecampStatic && !data.value) throw createError({ ...err.value!, fatal: true })
const staticEventFilters = data.value?.eventFilters
const noEventsInMarket = data.value?.noEventsInMarket
const loadMoreEvents = data.value?.loadMoreEvents

const ModalEventDetail = createModal('event-detail')
const ModalMobileFilters = createModal('mobile-filters')

const isUTMBVariant = computed(() => variant === 'UTMB')
const isBasecamp2026Variant = computed(() => variant === 'basecamp-2026')
const isLoyaltyDashboardVariant = computed(() => variant === 'loyalty-dashboard-event')
const isBasecamp2026SharedVariant = computed(() => isBasecamp2026Variant.value || isUTMBVariant.value)
const isBasecamp2026DialogVariant = computed(() =>
  isBasecamp2026SharedVariant.value || isLoyaltyDashboardVariant.value
)
const filterKeys = eventCollectionFilterKeys

const selectedFilters = reactive<FilterState>(createEmptyEventCollectionFilterState())

// Due to CoreMedia limitations, we need to transform the data on the client
// to better match used component props contract
const events: EventCollectionItem[] = items.map((item) => {
  const mediaFallback = item.mediaList?.[0]
    ? (() => {
        const { url: src, alt, width, height, ...rest } = swapObjectKeys(item.mediaList[0])
        return {
          ...rest,
          src,
          alt: getValueForBreakpoint('sm', alt) || '',
          width: getValueForBreakpoint('sm', width),
          height: getValueForBreakpoint('sm', height),
        }
      })()
    : undefined

  return {
    ...item as any,
    date: {
      start: item.startDate,
      end: item.endDate,
      startTime: item.startTime,
      endTime: item.endTime,
    },
    image: {
      ...item.image,
      ...mediaFallback,
      src: item.image?.url || mediaFallback?.src,
      alt: item.image?.alt || mediaFallback?.alt || '',
    },
    mediaList: (item.mediaList || []).map((image) => {
      const { url: src, alt, ...rest } = swapObjectKeys(image)
      return { ...rest, src, alt: getValueForBreakpoint('sm', alt) }
    }),
  }
})
const staticEventFilterDefinitions: StaticEventFilterDefinition[] = Array.isArray(staticEventFilters)
  ? staticEventFilters.map((filter) => ({
      label: typeof filter?.label === 'string' ? filter.label : '',
      options: Array.isArray(filter?.options)
        ? filter.options.filter((option): option is string => typeof option === 'string')
        : undefined,
    }))
  : []

const filterEngine = createEventCollectionFilterEngine({
  events,
  staticEventFilters: staticEventFilterDefinitions,
  userCountryCode,
})
const {
  hasEventsInUserCountry,
  initialCountrySelection,
  isPastEvent,
  matchesFilters,
  getEventFiltersForState,
} = filterEngine

// Override static CMS options with dynamic values from live event data
const eventFilters = computed<FilterDefinition[]>(() => getEventFiltersForState(selectedFilters))
if (initialCountrySelection.length) {
  selectedFilters.countries = initialCountrySelection
}

const open = async (event: EventCollectionItem) => {
  const modalProps = {
    event,
    variant,
    backgroundColor,
    isPastEvent: isPastEvent(event)
  }

  if (isLoyaltyDashboardVariant.value) {
    await ModalEventDetail.open(modalProps)
    return
  }

  ModalEventDetail.open(modalProps)
  eventQuery.value = event.id
}

if (!isLoyaltyDashboardVariant.value && eventQuery.value) {
  const event = events.find(({ id }) => id === eventQuery.value)
  if (event) ModalEventDetail.open({ event, variant, backgroundColor, isPastEvent: isPastEvent(event) })
}

const textColor = [
  ...backgroundColor?.sm ? [hasHighBrightness(backgroundColor.sm!) ? 'c-black!' : 'c-white!'] : [],
  ...backgroundColor?.md ? [hasHighBrightness(backgroundColor.md!) ? 'md:c-black!' : 'md:c-white!'] : [],
  ...backgroundColor?.lg ? [hasHighBrightness(backgroundColor.lg!) ? 'lg:c-black!' : 'lg:c-white!'] : [],
]

const translateWeekday = (weekdayKey: string) => {
  switch (weekdayKey) {
    case 'monday': return $t.monday
    case 'tuesday': return $t.tuesday
    case 'wednesday': return $t.wednesday
    case 'thursday': return $t.thursday
    case 'friday': return $t.friday
    case 'saturday': return $t.saturday
    case 'sunday': return $t.sunday
    default: return weekdayKey
  }
}

const utmbDateFilters = computed(() => createUTMBDateFilterOptions(events, $locale, translateWeekday))

const filters = computed(() => {
  if (isUTMBVariant.value) {
    return utmbDateFilters.value
  }

  return [...new Set(
    events
      .flatMap((event) => event.locations?.map((loc) => loc?.name) ?? [])
      .filter(Boolean)
  )].map((value) => ({ value, label: value }))
})

const truncatedFilters = computed(() => isViewMore.value ? filters.value : filters.value.slice(0, 3))
const viewMoreLinkCount = computed(() => isViewMore.value ? 0 : filters.value.slice(3, filters.value.length - 3).length)
const isSummitRunClubVariant = computed(() => variant === 'summit-running-club')
const hasActiveFilters = computed(() => filterKeys.some((key) => selectedFilters[key].length > 0))
const shouldShowNoEventsInMarket = computed(() =>
  Boolean(userCountryCode) && !hasEventsInUserCountry && !hasActiveFilters.value
)

const filteredEvents = computed(() => {
  if (isBasecamp2026Variant.value && shouldShowNoEventsInMarket.value) return []

  const filtered = events.filter((event) => {
    if (isLoyaltyDashboardVariant.value) {
      return true
    }

    if (isBasecamp2026Variant.value) {
      return matchesFilters(event, selectedFilters)
    }

    if (isUTMBVariant.value) {
      return activeFilter.value ? isEventInUTMBDateFilter(event, activeFilter.value) : true
    }

    return activeFilter.value ? event.locations?.some((loc) => loc?.name === activeFilter.value) : true
  })

  const activeEvents = filtered.filter((event) => !isPastEvent(event))
  const pastEvents = filtered.filter((event) => isPastEvent(event))

  const sortByDateAsc = (a: EventCollectionItem, b: EventCollectionItem) =>
    new Date(a.startDate).getTime() - new Date(b.startDate).getTime()

  const sortByDateDesc = (a: EventCollectionItem, b: EventCollectionItem) =>
    new Date(b.startDate).getTime() - new Date(a.startDate).getTime()

  activeEvents.sort(sortByDateAsc)
  pastEvents.sort(sortByDateDesc)

  const sortGroup = (group: EventCollectionItem[]) => {
    if (!proximityEnabled) return group
    return userCoordinates.value
      ? sortEventsByProximity(group, userCoordinates.value)
      : sortEventsByLocaleCountry(group, userCountryCode)
  }

  return [...sortGroup(activeEvents), ...sortGroup(pastEvents)]
})

// Load more events
const isLoyaltyDashboardExpanded = ref(false)
const visibleCount = ref(12)
const displayedEvents = computed(() => {
  if (isLoyaltyDashboardVariant.value) {
    return filteredEvents.value
  }

  return isBasecamp2026SharedVariant.value
    ? filteredEvents.value.slice(0, visibleCount.value)
    : filteredEvents.value
})
const hasMoreEvents = computed(() =>
  isBasecamp2026SharedVariant.value
  && filteredEvents.value.length > visibleCount.value
)
const hasMoreLoyaltyMobileEvents = computed(() =>
  !isLoyaltyDashboardExpanded.value && filteredEvents.value.length > 2
)
const hasMoreLoyaltyDesktopEvents = computed(() =>
  !isLoyaltyDashboardExpanded.value && filteredEvents.value.length > 4
)
const loadMore = () => {
  if (isLoyaltyDashboardVariant.value) {
    isLoyaltyDashboardExpanded.value = true
    return
  }

  visibleCount.value += 12
}

const formatLoyaltyLocation = (event: EventCollectionItem) => {
  const location = event.locations?.[0]
  if (!location) return event.locationName || ''
  const city = location.name
  const country = location.countryName || location.completeName
  if (city && country && city !== country) return `${city}, ${country}`
  return city || country || ''
}

watch(
  () => [
    selectedFilters.countries.join(),
    selectedFilters.eventTypes.join(),
    selectedFilters.eventStatuses.join(),
    activeFilter.value,
  ],
  () => {
    visibleCount.value = 12
    isLoyaltyDashboardExpanded.value = false
  }
)

// Basecamp2026 filter functions
const openMobileFilters = async () => {
  const result = await ModalMobileFilters.open({
    events,
    staticEventFilters: staticEventFilterDefinitions,
    initialDraft: selectedFilters,
  })
  if (result) {
    filterKeys.forEach((key) => {
      selectedFilters[key] = [...result[key]]
    })
  }
}

const clearAllFilters = () => {
  filterKeys.forEach((key) => {
    selectedFilters[key] = []
  })
}

const removeSelectedFilter = (key: typeof filterKeys[number], value: string) => {
  selectedFilters[key] = selectedFilters[key].filter((item) => item !== value)
}
</script>
